<template>
  <MinimalLayout
    :loading="step === 'loading'"
    :title="step === 'selectBank' && t('link.selectBank.title')"
  >
    <ExpiredSentStatus v-if="step === 'tokenExpired'" :message="t('link.expired')" />

    <SelectBank
      v-else-if="step === 'selectBank' && accountId"
      :account-id="accountId"
      :bank-selected="handleBankSelected"
    />

    <ErrorStatus v-else>{{ t("link.errorOccurred") }}</ErrorStatus>
  </MinimalLayout>
</template>

<script lang="ts" setup>
import ErrorStatus from "@/components/ErrorStatus.vue";
import ExpiredSentStatus from "@/components/ExpiredSentStatus.vue";
import MinimalLayout from "@/components/MinimalLayout.vue";
import { useI18n } from "@/composables/useI18n";
import { call, db } from "@/firebase";
import { useUserStore } from "@/store";
import SelectBank from "@/views/Link/components/SelectBank.vue";
import { tokenAuth } from "@/views/Link/tokenAuth";
import type { Account } from "@aircap/common/account";
import type { GclAccount } from "@aircap/common/gcl";
import { captureException } from "@sentry/vue";
import { doc, getDoc } from "firebase/firestore";
import { ref } from "vue";
import { useRoute } from "vue-router";
import { useFirebaseAuth } from "vuefire";

const { t } = useI18n();
const auth = useFirebaseAuth();
const route = useRoute();
const user = useUserStore();

const accountId = ref<string | null>(null);
const account = ref<Account | null>(null);
const step = ref<"loading" | "tokenExpired" | "error" | "selectBank">("loading");

const handleBankSelected = async (bankId: string) => {
  console.log(`Bank selected: ${bankId}`);
  try {
    if (!account.value || !accountId.value) throw new Error("Project not found");
    if (account.value.accountType === "gcl") {
      console.log(`Loading gcl link for project ${accountId.value}`);
      const link = await call("getGclLink")({
        institutionId: bankId,
        accountId: accountId.value,
      });
      console.log(`Redirecting to gcl link: ${link}`);
      window.location.replace(link);
    } else {
      throw new Error("Account type not supported");
    }
  } catch (cause) {
    const error = new Error("Error in Link Start handleBankSelected function", { cause });
    console.error(error);
    captureException(error);
    step.value = "error";
  }
};

void (async () => {
  try {
    if (!auth) throw new Error("Auth not initialized");

    if (route.query.token && typeof route.query.token === "string") {
      const result = await tokenAuth(auth, route.query.token);
      if (!result.success) {
        if (result.tokenExpired) {
          step.value = "tokenExpired";
          return;
        }
        throw result.error;
      }
    }

    await user.waitForLoading();

    if (!user.authenticatedUser) throw new Error("User not authenticated");

    if (user.authenticatedUser.authClaims.kind !== "operator")
      throw new Error("User is not operator");

    console.log(
      `Setting projectId from user claims: ${user.authenticatedUser.authClaims.accountId}`,
    );
    accountId.value = user.authenticatedUser.authClaims.accountId;

    const accountSnap = await getDoc(doc<Account>(db, "accounts", accountId.value));
    const _account = accountSnap.data();
    if (!_account) throw new Error(`Project ${accountId.value} not found`);
    account.value = _account;
    console.log(`Project ${accountId.value} loaded, type is ${account.value.accountType}`);

    switch (account.value.accountType) {
      case "bridge": {
        console.log(`Loading bridge link`);
        const link = await call("getBridgeLink")({ accountId: accountId.value });
        console.log(`Redirecting to bridge link: ${link}`);
        window.location.replace(link);
        break;
      }
      case "powens": {
        console.log(`Loading powens link`);
        const link = await call("getPowensLink")({ accountId: accountId.value });
        console.log(`Redirecting to powens link: ${link}`);
        window.location.replace(link);
        break;
      }
      case "gcl": {
        if (account.value.bankAccountId) {
          console.log(
            `project already has a bankAccountId ${account.value.bankAccountId} set, fetching its institution ID`,
          );

          const bankAccountSnap = await getDoc(
            doc<GclAccount>(
              db,
              "accounts",
              accountId.value,
              "gclAccounts",
              account.value.bankAccountId,
            ),
          );
          const bankAccount = bankAccountSnap.data();
          if (!bankAccount) throw new Error(`Account ${account.value.bankAccountId} not found`);
          console.log(
            `Account ${account.value.bankAccountId} loaded, institution is ${bankAccount.metadata.institution_id}`,
          );

          console.log(`Loading gcl link`, {
            institutionId: bankAccount.metadata.institution_id,
            accountId: accountId.value,
          });
          const link = await call("getGclLink")({
            institutionId: bankAccount.metadata.institution_id,
            accountId: accountId.value,
          });

          console.log(`Redirecting to gcl link: ${link}`);
          window.location.replace(link);
        } else {
          console.log(`No bankAccountId set, showing select bank step`);
          step.value = "selectBank";
        }
        break;
      }
      case "swan":
      case "treezor": {
        throw new Error("Account type not supported");
      }
    }
  } catch (cause) {
    const error = new Error("Error in Link Start init function", { cause });
    console.error(error);
    captureException(error);
    step.value = "error";
  }
})();
</script>
